import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { FeeService } from '../service/fee.service';
import { MemberPaymentPageResponse, MemberPaymentRecord, MemberSubgroupInfo } from '../interface/fee.interface';
import { PaymentMethodConfigService } from '../service/payment-method-config.service';
import { PaymentMethodConfig } from '../models/payment-method-config.model';
import { CreditCardService } from '../service/credit-card.service';
import { CreditCardResponse } from '../models/creditCardResponse';
import { WalletToWalletPaymentReceipt } from '../models/payment/walletToWalletPaymentReceipt';
import { UserService } from '../service/user.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { QrGenerateComponent } from '../qr-generate/qr-generate.component';
import { UserCardService } from '../service/user-card.service';
import { UserCardResponse } from '../interface/userCardResponse';
import { AmeriaWalletToWalletService } from '../service/ameria-wallet-to-wallet.service';
import { InitPaymentRequest } from '../models/payment/initPaymentRequest';
import { InitPaymentResponse } from '../models/payment/initPaymentResponse';
import { AMERIA_FORM_APPLE_PAY, AMERIA_FORM_CREDIT_CARD, PAYMENT_ID_NAME } from '../constants/payment.constant';
import { IdramService } from '../service/idram.service';
import { EasywalletService } from '../service/easywallet.service';
import { TelcellService } from '../service/telcell.service';
import { MyPayService } from '../service/my-pay.service';
import { FastshiftService } from '../service/fastshift.service';
import { MobileDetectionService } from '../service/mobile-detection.service';
import {PaymentReceiptType} from "../enums/payment-receipt-type.enum";
import { CLIPBOARD_SHORTCUT_KEYS, NAVIGATION_KEYS } from '../constants/keyboard.constants';

@Component({
  selector: 'app-fee-payment-form',
  templateUrl: './fee-payment-form.component.html',
  styleUrls: ['./fee-payment-form.component.scss']
})
export class FeePaymentFormComponent implements OnInit {
  private static readonly FEE_PAYMENT_CONTEXT = 'fee';
  private static readonly PAYMENT_RECEIPT_CONTEXT_KEY = 'payment_receipt_context';

  loading = true;
  error = false;
  paying = false;
  payError = false;
  page: MemberPaymentPageResponse | null = null;
  record: MemberPaymentRecord | null = null;
  subgroup: MemberSubgroupInfo | null = null;
  showOtherPaymentMethods = false;
  paymentMethodConfigs: PaymentMethodConfig[] = [];
  savedCards: CreditCardResponse[] = [];
  ownerCard: UserCardResponse | null = null;
  isMobile = false;
  payableAmount: number | null = null;
  payableAmountInput = '';
  showZeroAmountError = false;
  recurringPaymentEnabled = false;

  private token = '';
  private readonly payValidDays = 1;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly feeService: FeeService,
    private readonly paymentMethodConfigService: PaymentMethodConfigService,
    private readonly creditCardService: CreditCardService,
    private readonly userService: UserService,
    private readonly dialog: MatDialog,
    private readonly userCardService: UserCardService,
    private readonly ameriaWalletToWalletService: AmeriaWalletToWalletService,
    private readonly idramService: IdramService,
    private readonly easywalletService: EasywalletService,
    private readonly telcellService: TelcellService,
    private readonly myPayService: MyPayService,
    private readonly fastshiftService: FastshiftService
  ) {}

  ngOnInit(): void {
    this.isMobile = MobileDetectionService.isMobile();
    this.paymentMethodConfigService.getAll().subscribe({
      next: configs => this.paymentMethodConfigs = configs
    });
    // Only fetch saved cards when the user is authenticated — calling this
    // without a token triggers a 401, which causes AuthInterceptor to call
    // logout() and navigate away, breaking the public payment page.
    if (this.userService.token) {
      this.creditCardService.getAll().subscribe({
        next: cards => this.savedCards = cards ?? [],
        error: () => this.savedCards = []
      });
    }

    this.token = this.route.snapshot.paramMap.get('token') ?? '';
    const recordId = Number(this.route.snapshot.paramMap.get('recordId'));

    this.feeService.getPaymentPage(this.token).subscribe({
      next: (res) => {
        this.page = res;
        if (res.ownerCardId) {
          this.loadOwnerCard(res.ownerCardId);
        }
        this.record = res.paymentHistory.find(r => r.id === recordId) ?? null;
        if (this.record) {
          this.subgroup = res.subgroups.find(s => s.id === this.record!.subgroupId) ?? null;
        }
        if (res.tokenExpired) {
          this.router.navigate(['/error-page']);
          return;
        }
        const selectedRecord = res.paymentHistory.find(r => r.id === recordId) ?? null;
        if (!selectedRecord) {
          this.router.navigate(['/error-page']);
          return;
        }
        if (selectedRecord.status !== 'PENDING') {
          this.router.navigate(['/error-page']);
          return;
        }
        this.record = selectedRecord;
        this.subgroup = res.subgroups.find(s => s.id === selectedRecord.subgroupId) ?? null;
        this.payableAmount = selectedRecord.due;
        this.payableAmountInput = String(selectedRecord.due ?? '');
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 404 || error.status === 400) {
          this.router.navigate(['/error-page']);
          return;
        }
        this.error = true;
        this.loading = false;
      }
    });
  }

  private loadOwnerCard(ownerCardId: string): void {
    this.userCardService.getCardByQRCode(ownerCardId).subscribe({
      next: card => this.ownerCard = card,
      error: () => this.ownerCard = null
    });
  }

  get discountLabel(): string {
    if (!this.record || this.record.discount <= 0) return '—';
    if (this.record.discountType === 'PERCENT') {
      return `${this.record.discount} %`;
    }
    return `${this.record.discount.toLocaleString()} ֏`;
  }

  get periodDue(): number {
    return Math.max(0, this.record?.due ?? 0);
  }

  get hasSale(): boolean {
    return !!this.record && this.record.discount > 0;
  }

  get hasDue(): boolean {
    return this.periodDue > 0;
  }

  get isAmountEditable(): boolean {
    return !!this.subgroup?.allowCustomAmount;
  }

  onAmountKeyDown(event: KeyboardEvent): void {
    if (this.isKeyboardShortcut(event) || this.isNavigationKey(event.key)) {
      return;
    }

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    if (event.key === '0' && this.wouldCreateLeadingZero(event.target as HTMLInputElement)) {
      event.preventDefault();
      this.showZeroAmountError = true;
    }
  }

  onAmountPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const input = event.target as HTMLInputElement;
    const pastedValue = event.clipboardData?.getData('text') ?? '';
    const nextValue = this.mergeInputValue(input, pastedValue);
    this.applyAmountInput(input, nextValue);
  }

  onAmountInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.applyAmountInput(input, input.value);
  }

  get canSubmitPayment(): boolean {
    return this.getRequestedAmount() != null;
  }

  get isAlternatePaymentMethodDisabled(): boolean {
    return this.recurringPaymentEnabled || this.paying || !this.canSubmitPayment;
  }

  onRecurringPaymentToggle(enabled: boolean): void {
    this.recurringPaymentEnabled = enabled;
    if (enabled) {
      this.openOtherPaymentView();
    }
  }

  private getRequestedAmount(): number | null {
    const value = Number(this.payableAmount);
    if (!Number.isFinite(value) || value <= 0) {
      return null;
    }
    return Math.round(value);
  }

  private rejectZeroAmountSubmission(): boolean {
    if (this.canSubmitPayment) {
      return false;
    }
    this.showZeroAmountError = this.payableAmountInput === '0' || this.payableAmount === 0;
    return true;
  }

  private applyAmountInput(input: HTMLInputElement, value: string): void {
    const digitsOnly = (value || '').replace(/\D/g, '');
    const sanitizedValue = digitsOnly.replace(/^0+/, '');
    input.value = sanitizedValue;
    this.payableAmountInput = sanitizedValue;

    if (!sanitizedValue) {
      this.payableAmount = null;
      this.showZeroAmountError = digitsOnly.length > 0;
      return;
    }

    const parsed = Number(sanitizedValue);
    this.payableAmount = Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    this.showZeroAmountError = false;
  }

  private mergeInputValue(input: HTMLInputElement, insertedValue: string): string {
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    return `${input.value.slice(0, start)}${insertedValue}${input.value.slice(end)}`;
  }

  private wouldCreateLeadingZero(input: HTMLInputElement): boolean {
    const nextValue = this.mergeInputValue(input, '0');
    return !nextValue.replace(/\D/g, '').replace(/^0+/, '');
  }

  private isKeyboardShortcut(event: KeyboardEvent): boolean {
    return (event.ctrlKey || event.metaKey) && CLIPBOARD_SHORTCUT_KEYS.includes(event.key.toLowerCase());
  }

  private isNavigationKey(key: string): boolean {
    return NAVIGATION_KEYS.includes(key);
  }

  payWithNewCard(isApplePay: boolean = false): void {
    if (!this.record || !this.ownerCard || this.paying) return;
    if (this.rejectZeroAmountSubmission()) return;
    const requestedAmount = this.getRequestedAmount();
    if (requestedAmount == null) return;
    this.paying = true;
    this.payError = false;

    const request = InitPaymentRequest.initForWalletToWallet(
      `Fee payment for ${this.page?.memberName ?? 'member'}`,
      requestedAmount,
      this.ownerCard.userId,
      this.ownerCard.cardId,
      '',
      null,
      null,
      null,
      null,
      this.record.id,
      this.recurringPaymentEnabled
    );
    request.payWithNewCard = true;

    this.ameriaWalletToWalletService.init(request, true).subscribe({
      next: (paymentResponse: InitPaymentResponse) => {
        const formUrl = isApplePay ? AMERIA_FORM_APPLE_PAY : AMERIA_FORM_CREDIT_CARD;
        window.location.href = formUrl.replace(PAYMENT_ID_NAME, paymentResponse.PaymentID);
      },
      error: () => {
        this.paying = false;
        this.payError = true;
      },
      complete: () => {
        this.paying = false;
      }
    });
  }

    payWithSavedCard(cardHolderId: string): void {
    if (!this.record || !this.ownerCard || this.paying) return;
    if (this.rejectZeroAmountSubmission()) return;
    const requestedAmount = this.getRequestedAmount();
    if (requestedAmount == null) return;
    this.paying = true;
    this.payError = false;

    const request = InitPaymentRequest.initForWalletToWallet(
      `Fee payment for ${this.page?.memberName ?? 'member'}`,
      requestedAmount,
      this.ownerCard.userId,
      this.ownerCard.cardId,
      cardHolderId,
      null,
      null,
      null,
      null,
      this.record.id,
      this.recurringPaymentEnabled
    );

    this.ameriaWalletToWalletService.makeBindingPayment(request, true).subscribe({
      next: (receipt: WalletToWalletPaymentReceipt) => {
        this.markFeePaymentContext();
        const memberId = this.page?.memberId;
        const subgroupId = this.record?.subgroupId;
        const backUrl = memberId
          ? `/fees?membershipId=${memberId}${subgroupId ? `&subgroupId=${subgroupId}` : ''}`
          : '/fees';
        sessionStorage.setItem('fee_back_url', backUrl);
        this.router.navigate(['/payment/receipt'], {
          state: { receipt, backUrl, paymentContext: FeePaymentFormComponent.FEE_PAYMENT_CONTEXT }
        });
      },
      error: () => {
        this.paying = false;
        this.payError = true;
      },
      complete: () => {
        this.paying = false;
      }
    });
  }

      payWithIdram(): void {
        if (!this.record || !this.ownerCard || this.paying) return;
        if (this.rejectZeroAmountSubmission()) return;
        const requestedAmount = this.getRequestedAmount();
        if (requestedAmount == null) return;
        this.paying = true;
        this.payError = false;
        this.idramService.initPayment(this.ownerCard, String(requestedAmount), `Fee payment for ${this.page?.memberName ?? 'member'}`, true, null);
        this.paying = false;
      }

      payWithEasyWallet(): void {
        if (!this.record || !this.ownerCard || this.paying) return;
        if (this.rejectZeroAmountSubmission()) return;
        const requestedAmount = this.getRequestedAmount();
        if (requestedAmount == null) return;
        this.paying = true;
        this.payError = false;
        this.easywalletService.initPayment(
          String(requestedAmount),
          `Fee payment for ${this.page?.memberName ?? 'member'}`,
          String(this.payValidDays),
          this.ownerCard,
          true,
          null
        );
        this.paying = false;
      }

      payWithTelcell(): void {
        if (!this.record || !this.ownerCard || this.paying) return;
        if (this.rejectZeroAmountSubmission()) return;
        const requestedAmount = this.getRequestedAmount();
        if (requestedAmount == null) return;
        this.paying = true;
        this.payError = false;

        if (!this.isMobile) {
          this.telcellService.initPayment(
            String(requestedAmount),
            `Fee payment for ${this.page?.memberName ?? 'member'}`,
            String(this.payValidDays),
            this.ownerCard,
            true,
            null
          );
          this.paying = false;
          return;
        }

        this.telcellService.createNewAccount(String(requestedAmount), this.ownerCard.cardId, true, null).subscribe({
          next: (invoiceId: string) => {
            window.location.href = `https://telcellwallet.page.link/?link=https://com.telcell.app?actionType%3DinvoiceId%26actionValue%3D${invoiceId}&apn=ru.deltapay.puntopago.telcell&isi=1324511564&ibi=com.telcell.app`;
            this.paying = false;
          },
          error: () => {
            this.payError = true;
            this.paying = false;
          },
          complete: () => {
            this.paying = false;
          }
        });
      }

      payWithMyPay(): void {
        if (!this.record || !this.ownerCard || this.paying) return;
        if (this.rejectZeroAmountSubmission()) return;
        const requestedAmount = this.getRequestedAmount();
        if (requestedAmount == null) return;
        this.paying = true;
        this.payError = false;
        this.myPayService.initPayment(String(requestedAmount), this.ownerCard, true);
        this.paying = false;
      }

      payWithFastshift(): void {
        if (!this.record || !this.ownerCard || this.paying) return;
        if (this.rejectZeroAmountSubmission()) return;
        const requestedAmount = this.getRequestedAmount();
        if (requestedAmount == null) return;
        this.paying = true;
        this.payError = false;
        this.fastshiftService.initPayment(
          String(requestedAmount),
          `Fee payment for ${this.page?.memberName ?? 'member'}`,
          String(this.payValidDays),
          this.ownerCard,
          true,
          null
        );
        this.paying = false;
      }

      toggleOtherPayments(): void {
        this.showOtherPaymentMethods = !this.showOtherPaymentMethods;
      }

      openOtherPaymentView(): void {
        this.showOtherPaymentMethods = true;
      }

      shareLink(): void {
        const shareUrl = window.location.href;
        navigator.clipboard.writeText(shareUrl).then(() => {
          if (navigator.share) {
            navigator.share({title: 'Payment Link', url: shareUrl});
          }
        });
      }

      showQr(): void {
        const paymentUrl = window.location.href;
        this.feeService.generatePaymentQrCode(paymentUrl).subscribe((qrBlob: Blob) => {
          const qrUrl = URL.createObjectURL(qrBlob);
          const dialogConfig = new MatDialogConfig();
          dialogConfig.data = {
            userCard: {
              name: this.page?.memberName ?? '',
              surname: '',
              specialization: '',
              company: '',
              qrUsername: ''
            },
            qrCodeUrl: qrUrl,
            customUrl: paymentUrl
          };
          dialogConfig.panelClass = 'qr-dialog';
          this.dialog.open(QrGenerateComponent, dialogConfig);
        });
      }

      goBack(): void {
        const backUrl = sessionStorage.getItem('fee_back_url');
        if (backUrl) {
          this.router.navigateByUrl(backUrl);
          return;
        }
        this.router.navigate([this.userService.isLoggedIn() ? '/fees' : '/login']);
      }

      getLogoUrl(code: string, fallback: string): string {
        const config = this.paymentMethodConfigs.find(m => m.code === code);
        return config?.logoUrl || fallback;
      }

      maskCardNumber(cardNumber: string | undefined): string {
        if (!cardNumber) return '****';
        const tail = cardNumber.slice(-4);
        return `**** ${tail}`;
      }

  private markFeePaymentContext(): void {
    sessionStorage.setItem(
      FeePaymentFormComponent.PAYMENT_RECEIPT_CONTEXT_KEY,
      FeePaymentFormComponent.FEE_PAYMENT_CONTEXT
    );
  }
}
